/**
 * TechPayrollReport (PR-04 v1) — the first HR & payroll surface: per-tech
 * hours + invoice tickets over a date range, straight from ESC's recorded
 * data (mirror_sales_emp ⨝ mirror_sales). What HR pulls per pay period:
 * who worked what tickets, reg/OT hours, labor cost — summary per tech,
 * expandable to the ticket detail, CSV of the detail rows.
 *
 * Deliberately READ-ONLY and recorded-data-only per §PR sequencing: real
 * timesheets ride the mobile app's geolocation punches; commission math
 * rides pay profiles + the invoice builder. No derived pay numbers here —
 * only what ESC actually recorded ([[feedback_derive_dont_flag]]'s stated
 * side: report the record, never guess pay).
 */
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { format, parseISO } from "date-fns";
import { ChevronDown, ChevronRight, HardHat } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/Card";
import { InfoTip } from "@/components/InfoTip";
import { cn } from "@/lib/cn";
import { supabase } from "@/lib/supabase";
import { pagedAll } from "@/lib/pagedAll";
import { deptLabel } from "@/lib/departments";
import {
  isoDaysAgo,
  isoToday,
  DateRangeControl,
  CsvButton,
  downloadCsv,
} from "./listCore";
import { PayWeekEngine } from "./PayWeekEngine";

// ESC's pseudo-employee holding buckets are not payroll ([[reference_pseudo_employees]]).
const PSEUDO = new Set(["0000", "0001", "1000", "1001", "1002", "1003", "7777", "8888", "9999"]);

interface DetailRow {
  empno: string;
  invoice: string;
  dispatch: string | null;
  inv_date: string | null;
  dept: string | null;
  inv_amount: number | null;
  reg_hr: number;
  ot_hr: number;
  lab_amt: number;
  credit_pct: number;
}

interface TechSummary {
  empno: string;
  name: string;
  tickets: number;
  reg_hr: number;
  ot_hr: number;
  lab_amt: number;
  billed: number;
  rows: DetailRow[];
}

function chunk<T>(arr: T[], n: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < arr.length; i += n) out.push(arr.slice(i, i + n));
  return out;
}

function usePayrollData(from: string, to: string) {
  return useQuery({
    queryKey: ["tech_payroll", from, to],
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<TechSummary[]> => {
      // 1) Invoices in range (header) — pagedAll: ranges blow past the cap.
      const sales = await pagedAll<{
        invoice: string; dispatch: string | null; inv_date: string | null;
        dept: string | null; inv_amount: number | null;
      }>(() =>
        supabase
          .from("mirror_sales")
          .select("invoice, dispatch, inv_date, dept, inv_amount")
          .gte("inv_date", from)
          .lte("inv_date", to)
          .order("invoice", { ascending: true }),
      );
      const byInvoice = new Map(sales.map((s) => [s.invoice, s]));
      const invoices = [...byInvoice.keys()];
      if (invoices.length === 0) return [];

      // 2) Per-tech rows for those invoices (chunked .in()).
      const emp: {
        invoice: string; empno: string | null; credit_pct: number | null;
        reg_hr: number | null; ot_hr: number | null; lab_amt: number | null;
        dispatch: string | null;
      }[] = [];
      for (const c of chunk(invoices, 200)) {
        const { data, error } = await supabase
          .from("mirror_sales_emp")
          .select("invoice, empno, credit_pct, reg_hr, ot_hr, lab_amt, dispatch")
          .in("invoice", c);
        if (error) throw error;
        emp.push(...((data ?? []) as typeof emp));
      }

      // 3) Names.
      const empnos = [...new Set(emp.map((e) => e.empno).filter((e): e is string => !!e && !PSEUDO.has(e)))];
      const { data: emps } = await supabase
        .from("mirror_employee")
        .select("empno, empname")
        .in("empno", empnos.length > 0 ? empnos : ["__none__"]);
      const names = new Map(
        ((emps ?? []) as { empno: string; empname: string | null }[]).map((e) => [e.empno, e.empname ?? e.empno]),
      );

      // 4) Aggregate per tech.
      const byTech = new Map<string, TechSummary>();
      for (const e of emp) {
        if (!e.empno || PSEUDO.has(e.empno)) continue;
        const s = byInvoice.get(e.invoice);
        if (!s) continue;
        const row: DetailRow = {
          empno: e.empno,
          invoice: e.invoice,
          dispatch: e.dispatch ?? s.dispatch,
          inv_date: s.inv_date,
          dept: s.dept,
          inv_amount: s.inv_amount,
          reg_hr: e.reg_hr ?? 0,
          ot_hr: e.ot_hr ?? 0,
          lab_amt: e.lab_amt ?? 0,
          credit_pct: e.credit_pct ?? 0,
        };
        const g = byTech.get(e.empno);
        if (g) {
          g.tickets += 1; g.reg_hr += row.reg_hr; g.ot_hr += row.ot_hr;
          g.lab_amt += row.lab_amt; g.billed += row.inv_amount ?? 0;
          g.rows.push(row);
        } else {
          byTech.set(e.empno, {
            empno: e.empno,
            name: names.get(e.empno) ?? e.empno,
            tickets: 1,
            reg_hr: row.reg_hr, ot_hr: row.ot_hr, lab_amt: row.lab_amt,
            billed: row.inv_amount ?? 0,
            rows: [row],
          });
        }
      }
      const out = [...byTech.values()];
      for (const t of out) t.rows.sort((a, b) => (a.inv_date ?? "").localeCompare(b.inv_date ?? ""));
      out.sort((a, b) => b.reg_hr + b.ot_hr - (a.reg_hr + a.ot_hr));
      return out;
    },
  });
}

const money = (v: number | null) =>
  v === null ? "—" : v.toLocaleString("en-US", { style: "currency", currency: "USD" });
const hrs = (v: number) => (Math.round(v * 100) / 100).toLocaleString();

export function TechPayrollReport() {
  // PR-05 (Seth 2026-08-21): the PAY WEEK engine is the payroll ritual —
  // it opens first. The raw ticket-detail range view stays one click away.
  const [mode, setMode] = useState<"week" | "detail">("week");
  const [from, setFrom] = useState(isoDaysAgo(14));
  const [to, setTo] = useState(isoToday());
  const { data, isLoading } = usePayrollData(from, to);
  const techs = data ?? [];
  const [open, setOpen] = useState<Set<string>>(new Set());
  const navigate = useNavigate();

  const totals = useMemo(
    () =>
      techs.reduce(
        (a, t) => ({
          tickets: a.tickets + t.tickets,
          reg: a.reg + t.reg_hr,
          ot: a.ot + t.ot_hr,
          labor: a.labor + t.lab_amt,
        }),
        { tickets: 0, reg: 0, ot: 0, labor: 0 },
      ),
    [techs],
  );

  function exportCsv() {
    const rows: (string | number)[][] = [];
    for (const t of techs) {
      for (const r of t.rows) {
        rows.push([
          t.name, t.empno, r.invoice, r.dispatch ?? "", r.inv_date ?? "",
          deptLabel(r.dept), r.reg_hr, r.ot_hr,
          r.lab_amt, r.credit_pct || "", r.inv_amount ?? "",
        ]);
      }
    }
    downloadCsv(
      `tech-payroll-${from}-to-${to}.csv`,
      ["Tech", "Empno", "Invoice", "Dispatch", "Date", "Dept", "Reg hrs", "OT hrs", "Labor $", "% credit", "Invoice $"],
      rows,
    );
  }

  const toggle = (empno: string) => {
    const next = new Set(open);
    if (next.has(empno)) next.delete(empno);
    else next.add(empno);
    setOpen(next);
  };

  const modeBar = (
    <div className="mb-3 inline-flex items-center overflow-hidden rounded-lg border border-hairline bg-white shadow-e1">
      {([["week", "Pay Week (RHP)"], ["detail", "Ticket Detail"]] as const).map(([k, label], i) => (
        <button key={k} type="button" onClick={() => setMode(k)}
          className={
            (i > 0 ? "border-l border-hairline " : "") +
            "px-3 py-1.5 text-xs font-semibold transition-colors " +
            (mode === k ? "bg-brand-700 text-white" : "text-slate-600 hover:bg-slate-50")
          }>
          {label}
        </button>
      ))}
    </div>
  );

  if (mode === "week") {
    return (
      <div className="min-w-0 flex-1">
        {modeBar}
        <PayWeekEngine />
      </div>
    );
  }

  return (
    <div className="min-w-0 flex-1">
      {modeBar}
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <DateRangeControl from={from} to={to} onChange={(f, t) => { setFrom(f); setTo(t); }} />
        <div className="ml-auto"><CsvButton onClick={exportCsv} /></div>
      </div>

      {isLoading ? (
        <Card className="h-40 animate-pulse bg-slate-100" />
      ) : techs.length === 0 ? (
        <Card className="p-8 text-center text-sm text-slate-500">
          No invoiced tech hours in this range.
        </Card>
      ) : (
        <Card className="overflow-hidden">
          <div className="flex items-center gap-2 border-b border-slate-100 bg-gradient-to-r from-emerald-50/70 via-transparent to-transparent px-4 py-2.5">
            <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-emerald-50 text-emerald-600">
              <HardHat className="h-3.5 w-3.5" />
            </span>
            <span className="text-[14.5px] font-bold tracking-tight text-slate-900">
              Tech Hours &amp; Tickets
            </span>
            <InfoTip label="Where these numbers come from">
              Hours, labor cost and % credit exactly as recorded in ESC, with no pay math added.
            </InfoTip>
            <span className="ml-auto text-xs tabular-nums text-slate-500">
              {techs.length} techs · {totals.tickets} tickets · {hrs(totals.reg)}h reg
              {totals.ot > 0 ? ` + ${hrs(totals.ot)}h OT` : ""} · {money(totals.labor)} labor
            </span>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-100 text-left text-[12px] font-semibold text-slate-500">
                <th className="px-4 py-2">Tech</th>
                <th className="px-2 py-2 text-right">Tickets</th>
                <th className="px-2 py-2 text-right">Reg hrs</th>
                <th className="px-2 py-2 text-right">OT hrs</th>
                <th className="px-2 py-2 text-right">Labor $</th>
                <th className="px-4 py-2 text-right">Billed on their tickets</th>
              </tr>
            </thead>
            <tbody>
              {techs.map((t) => (
                <TechRows
                  key={t.empno}
                  t={t}
                  open={open.has(t.empno)}
                  onToggle={() => toggle(t.empno)}
                  onOpenInvoice={(inv) => navigate(`/invoice/${inv}?from=reports`)}
                />
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}

function TechRows({
  t,
  open,
  onToggle,
  onOpenInvoice,
}: {
  t: TechSummary;
  open: boolean;
  onToggle: () => void;
  onOpenInvoice: (invoice: string) => void;
}) {
  return (
    <>
      <tr
        onClick={onToggle}
        className="cursor-pointer border-b border-slate-50 transition-colors hover:bg-brand-50/40"
      >
        <td className="px-4 py-2 font-medium text-slate-800">
          <span className="inline-flex items-center gap-1.5">
            {open ? (
              <ChevronDown className="h-3.5 w-3.5 text-slate-400" />
            ) : (
              <ChevronRight className="h-3.5 w-3.5 text-slate-300" />
            )}
            {t.name}
          </span>
        </td>
        <td className="px-2 py-2 text-right tabular-nums">{t.tickets}</td>
        <td className="px-2 py-2 text-right tabular-nums">{hrs(t.reg_hr)}</td>
        <td className={cn("px-2 py-2 text-right tabular-nums", t.ot_hr > 0 && "font-medium text-amber-700")}>
          {t.ot_hr > 0 ? hrs(t.ot_hr) : "—"}
        </td>
        <td className="px-2 py-2 text-right tabular-nums">{money(t.lab_amt)}</td>
        <td className="px-4 py-2 text-right tabular-nums text-slate-500">{money(t.billed)}</td>
      </tr>
      {open &&
        t.rows.map((r) => (
          <tr
            key={`${r.invoice}-${r.empno}`}
            onClick={() => onOpenInvoice(r.invoice)}
            title="Open the invoice"
            className="cursor-pointer border-b border-slate-50 bg-slate-50/50 text-xs transition-colors hover:bg-brand-50/40"
          >
            <td className="py-1.5 pl-12 pr-4 text-slate-600">
              <span className="font-mono">{r.invoice}</span>
              {r.inv_date && (
                <span className="ml-2 text-slate-400">
                  {format(parseISO(r.inv_date), "MMM d, yyyy")}
                </span>
              )}
              {r.dept && (
                <span className="ml-2 rounded bg-slate-100 px-1 text-[10px] text-slate-500">
                  {deptLabel(r.dept)}
                </span>
              )}
              {r.credit_pct > 0 && (
                <span className="ml-2 rounded bg-violet-50 px-1 text-[10px] font-semibold text-violet-700">
                  {r.credit_pct}% credit
                </span>
              )}
            </td>
            <td className="px-2 py-1.5 text-right tabular-nums text-slate-400">—</td>
            <td className="px-2 py-1.5 text-right tabular-nums">{hrs(r.reg_hr)}</td>
            <td className="px-2 py-1.5 text-right tabular-nums">{r.ot_hr > 0 ? hrs(r.ot_hr) : "—"}</td>
            <td className="px-2 py-1.5 text-right tabular-nums">{money(r.lab_amt)}</td>
            <td className="px-4 py-1.5 text-right tabular-nums text-slate-400">{money(r.inv_amount)}</td>
          </tr>
        ))}
    </>
  );
}
